import { useQuery, type ApolloError, DocumentNode, WatchQueryFetchPolicy } from "@apollo/client";

import { FilterParams, OperatorType, QueryParams, PaginationResponse } from "../../RVGridTypes";
import { useQueryParamsV6 } from "../useQueryParams/useQueryParams";

type HookProps<TData> = {
    query: DocumentNode;
    defaultParams: Partial<QueryParams>;
    queryName: string;
    searchableFields: Array<string>;
    fetchPolicy?: WatchQueryFetchPolicy;
    initialState?: PaginationResponse<TData>;
    pollInterval?: number;
    optionalParams?: object;
    skip?: boolean;
    onError?: (error: ApolloError) => void;
    onCompleted?: (data: Record<string, PaginationResponse<TData>>) => void;
};

/**
 * Normalizes legacy "createdInfo"/"updatedInfo" keys in sort/filter params so the API receives valid field names.
 */
export function replaceInfoParams(obj: Partial<QueryParams>) {
    if (Array.isArray(obj.orderBy)) {
        obj.orderBy = obj.orderBy.map((order) => {
            if (order.key === "createdInfo") {
                return { ...order, key: "createdAt" };
            }
            if (order.key === "updatedInfo") {
                return { ...order, key: "updatedAt" };
            }
            return order;
        });
    }
    if (Array.isArray(obj.filterBy)) {
        obj.filterBy = obj.filterBy.map((order) => {
            if (order.key === "createdInfo") {
                return { ...order, key: "createdBy" };
            }
            if (order.key === "updatedInfo") {
                return { ...order, key: "updatedBy" };
            }
            return order;
        });
    }
    return obj;
}

const STABLE_DEFAULT_INITIAL_STATE: PaginationResponse<unknown> = {
    page: 0,
    pageSize: 25,
    items: [],
    total: 0,
    hasMore: undefined,
};

function getTypedInitialState<TData>(): PaginationResponse<TData> {
    return STABLE_DEFAULT_INITIAL_STATE as PaginationResponse<TData>;
}

/**
 * Wraps an Apollo query with shared pagination + search behavior for RV grids.
 *
 * @param query - GraphQL document that returns a paginated dataset.
 * @param defaultParams - Initial query params (page, pageSize, filters, etc.).
 * @param queryName - Object key in the Apollo response to read the pagination payload from.
 * @param searchableFields - Column keys that should respond to the search box.
 * @param fetchPolicy - Optional override for Apollo fetch policy.
 * @param initialState - Fallback pagination state while the first request resolves.
 * @param pollInterval - Optional polling duration.
 * @param optionalParams - Extra variables merged into the query (brandId, etc.).
 * @param skip - Skips the Apollo query entirely when true.
 * @param onCompleted - Callback fired once data arrives.
 */
export function useSearchableColumns<TData>({
    query,
    defaultParams,
    queryName,
    searchableFields,
    fetchPolicy = "cache-first",
    initialState = getTypedInitialState<TData>(),
    pollInterval,
    optionalParams = {},
    skip = false,
    onError,
    onCompleted,
}: HookProps<TData>) {
    const { queryParams, setUiQueryParams } = useQueryParamsV6(defaultParams);
    const updatedQueryParams = replaceInfoParams(queryParams);

    const variables = { queryParams: updatedQueryParams, ...optionalParams };
    const { loading, data, refetch, stopPolling } = useQuery<Record<string, PaginationResponse<TData>>>(query, {
        variables,
        skip: !updatedQueryParams || skip,
        errorPolicy: "all",
        fetchPolicy,
        pollInterval,
        onError,
        onCompleted,
    });

    const columnsData: PaginationResponse<TData> = data?.[queryName] || initialState;

    function getSearchableFilters(value: string): FilterParams[] | undefined {
        if (!value?.trim()) {
            return undefined;
        }

        return searchableFields.map((key) => {
            return {
                key,
                operator: OperatorType.CONTAINS,
                value,
            } as FilterParams;
        });
    }

    function setFilterValue(value: string): void {
        const filterBy = getSearchableFilters(value);

        setUiQueryParams((prevParams) => ({
            ...prevParams,
            page: 1,
            filterBy: [
                ...(prevParams.filterBy || []).filter((item) => !searchableFields.includes(item.key)),
                ...(filterBy || []),
            ],
        }));
    }

    return {
        stopPolling,
        loading,
        columnsData,
        refetch,
        setFilterValue,
    };
}
